import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Trash2, LoaderCircle, AlertCircle, Plus, FileText, CheckCircle, Info, ArrowLeft, Merge, Scissors } from 'lucide-react';
import ActionButton from './../../components/ActionButton.jsx';
import { loadCategories, startAnalysis, saveCategoryChanges, loadCategoryAnswers } from '../../api/BackendApi.jsx';

const normalizeCategoryLists = (categories) => {
  if (!Array.isArray(categories)) {
    return [];
  }
  if (categories.length === 0) {
    return [];
  }

  const isListOfLists = Array.isArray(categories[0]);
  return isListOfLists ? categories : [categories];
};

const normalizeIndefinites = (items) => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      if (Array.isArray(item)) {
        return [String(item[0] ?? ''), Number(item[1] ?? 1)];
      }

      if (typeof item === 'object' && item !== null) {
        return [
          String(item.answer ?? item.text ?? ''),
          Number(item.count ?? 1),
        ];
      }

      return [String(item ?? ''), 1];
    })
    .filter(([answer]) => answer.trim().length > 0);
};

const mergeIndefinites = (currentItems, newItems) => {
  const merged = new Map();

  normalizeIndefinites(currentItems).forEach(([answer, count]) => {
    merged.set(answer, (merged.get(answer) || 0) + count);
  });

  normalizeIndefinites(newItems).forEach(([answer, count]) => {
    merged.set(answer, (merged.get(answer) || 0) + count);
  });

  return Array.from(merged.entries()).sort((a, b) => b[1] - a[1]);
};

const TabToggle = ({ activeTab, onChange, indefiniteCount }) => (
  <div className="flex w-full mb-5 rounded-lg bg-gray-200 p-1">
    <button
      type="button"
      onClick={() => onChange('categories')}
      className={`flex-1 flex items-center justify-center p-2 rounded-md font-semibold transition-all ${
        activeTab === 'categories'
          ? 'bg-white text-blue-600 shadow-sm'
          : 'bg-transparent text-gray-600 hover:bg-gray-100'
      }`}
    >
      Kategorie
    </button>

    <button
      type="button"
      onClick={() => onChange('indefinites')}
      className={`flex-1 flex items-center justify-center p-2 rounded-md font-semibold transition-all ${
        activeTab === 'indefinites'
          ? 'bg-white text-blue-600 shadow-sm'
          : 'bg-transparent text-gray-600 hover:bg-gray-100'
      }`}
    >
      Nezařazené odpovědi ({indefiniteCount} různých)
    </button>
  </div>
);

const CategoriesStep = ({ onShowResult, fileId, updateBalance}) => {
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [originalCategories, setOriginalCategories] = useState([]);
  const [categoryRenames, setCategoryRenames] = useState([]);
  const [question, setQuestion] = useState('');
  const [indefinites, setIndefinites] = useState(null);
  const [currentCategory, setCurrentCategory] = useState('');

  const [activeTab, setActiveTab] = useState('categories');
  const [openCategoryMenuAnswer, setOpenCategoryMenuAnswer] = useState(null);
  const [answerCategoryAssignments, setAnswerCategoryAssignments] = useState([]);
  const [deletedCategories, setDeletedCategories] = useState([]);

  const [openFixedCategoryMenuAnswer, setOpenFixedCategoryMenuAnswer] = useState(null);
  const [fixedCategorySearch, setFixedCategorySearch] = useState('');
  const fixedCategoryMenuRef = useRef(null);

  const [suggestedCategoryLists, setSuggestedCategoryLists] = useState([]);
  const [isSelectingList, setIsSelectingList] = useState(false);
  const [expandedListIndex, setExpandedListIndex] = useState(null);

  const [prevCategories, setPrevCategories] = useState([]);
  const [isSelectingPrevOrNew, setIsSelectingPrevOrNew] = useState(false);
  const [pendingSuggestedLists, setPendingSuggestedLists] = useState([]);

  const [activeTool, setActiveTool] = useState('add-single');
  const [selectedForMerge, setSelectedForMerge] = useState([]);

  const [bulkText, setBulkText] = useState('');
  const [bulkPreview, setBulkPreview] = useState([]);

  const [categorySearch, setCategorySearch] = useState('');
  const categoryMenuRef = useRef(null);
  const [isBulkEditMode, setIsBulkEditMode] = useState(false);
  const [selectedBulkAnswers, setSelectedBulkAnswers] = useState([]);
  const [openBulkCategoryMenu, setOpenBulkCategoryMenu] = useState(false);
  const [bulkCategorySearch, setBulkCategorySearch] = useState('');
  const bulkCategoryMenuRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const successTimeoutRef = useRef(null);

  const isMounted = useRef(true);

  const indefiniteCount = indefinites?.length || 0;

  const isStartEnabled = useMemo(() => {
    if (isLoading || isSuggesting || isSelectingList || isSelectingPrevOrNew) return false;
    return categories.length > 0;
  }, [categories, isLoading, isSuggesting, isSelectingList, isSelectingPrevOrNew]);

  const showSuccessMessage = useCallback((message) => {
    setSuccessMessage(message);

    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);

    successTimeoutRef.current = setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  }, []);

  const updateAssignedAnswersCategory = useCallback((oldCategory, newCategory) => {
    if (!oldCategory || !newCategory || oldCategory === newCategory) return;

    setAnswerCategoryAssignments((prev) =>
      prev.map((assignment) =>
        assignment.category === oldCategory
          ? { ...assignment, category: newCategory }
          : assignment
      )
    );
  }, []);
  const removeAssignedAnswersForCategories = useCallback((removedCategories) => {
    const removedSet = new Set(removedCategories);

    setAnswerCategoryAssignments((prev) =>
      prev.filter((assignment) => !removedSet.has(assignment.category))
    );
  }, []);

  const getChangesPayload = useCallback(() => {
    const validCategories = new Set(categories);

    const validAnswerCategoryAssignments = answerCategoryAssignments.filter(
      (assignment) => validCategories.has(assignment.category)
    );

    return {
      categories,
      renamed_categories: categoryRenames,
      deleted_categories: deletedCategories,
      answer_category_assignments: validAnswerCategoryAssignments,
    };
  }, [categories, categoryRenames, deletedCategories, answerCategoryAssignments]);

  const handleAddCategory = useCallback(() => {
    if (currentCategory.trim()) {
      setCategories((prev) => [currentCategory.trim(), ...prev]);
      setCurrentCategory('');
    }
  }, [currentCategory]);

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text');

    if (paste.match(/[\n\t]/)) {
      e.preventDefault();

      const newCategories = paste
        .split(/[\n\t]+/)
        .map((cat) => cat.trim())
        .filter((cat) => cat.length > 0 && cat.length <= 255);

      if (newCategories.length > 0) {
        setCategories((prev) => [...prev, ...newCategories]);
        setCurrentCategory('');
      }
    }
  };

  const applySuggestedLists = useCallback((lists) => {
    if (lists.length === 1) {
      setCategories(lists[0]);
      setOriginalCategories(lists[0]);
      setSuggestedCategoryLists([]);
      setIsSelectingList(false);
    } else if (lists.length > 1) {
      setSuggestedCategoryLists(lists);
      setIsSelectingList(true);
    } else {
      setCategories([]);
      setOriginalCategories([]);
      setSuggestedCategoryLists([]);
      setIsSelectingList(false);
    }

    setIsSelectingPrevOrNew(false);
    setIsSuggesting(false);
  }, []);

  const handleSelectList = useCallback((selectedList) => {
    setCategories(selectedList);
    setOriginalCategories(selectedList);
    setIsSelectingList(false);
  }, []);

  const handleUsePrevCategories = useCallback(() => {
    setCategories(prevCategories);
    setOriginalCategories(prevCategories);
    setSuggestedCategoryLists([]);
    setPendingSuggestedLists([]);
    setIsSelectingList(false);
    setIsSelectingPrevOrNew(false);
    setIsSuggesting(false);
  }, [prevCategories]);

  const handleUseNewCategories = useCallback(() => {
    applySuggestedLists(pendingSuggestedLists);
    setPendingSuggestedLists([]);
  }, [applySuggestedLists, pendingSuggestedLists]);

  const handleProcessBulkText = () => {
    const parsed = bulkText
      .split(/[\n\t]+/)
      .map((cat) => cat.trim())
      .filter((cat) => cat.length > 0 && cat.length <= 255);

    setBulkPreview(parsed);
  };

  const handleConfirmBulkAdd = () => {
    if (bulkPreview.length > 0) {
      setCategories((prev) => [...prev, ...bulkPreview]);
      setBulkText('');
      setBulkPreview([]);
      setActiveTool('add-single');
    }
  };

  const handleRemoveCategory = useCallback(async (indexToRemove) => {
    const removedCategory = categories[indexToRemove];

    setCategories((prev) => prev.filter((_, index) => index !== indexToRemove));
    setSelectedForMerge([]);
    setDeletedCategories((prev) => (
      prev.includes(removedCategory) ? prev : [...prev, removedCategory]
    ));

    setCategoryRenames((prev) =>
      prev.filter((rename) => rename.old_name !== removedCategory && rename.new_name !== removedCategory)
    );

    removeAssignedAnswersForCategories([removedCategory]);

    if (indefinites !== null && removedCategory) {
      try {
        const response = await loadCategoryAnswers({
          fileId,
          category: removedCategory,
        });

        const answersFromBackend =
          response?.answers ||
          response?.indefinites ||
          response?.uncategorized_answers ||
          [];

        if (answersFromBackend.length > 0) {
          setIndefinites((prev) => mergeIndefinites(prev || [], answersFromBackend));
          showSuccessMessage(`Odpovědi z kategorie '${removedCategory}' byly přesunuty mezi nezařazené.`);
        }
      } catch (error) {
        console.error('Failed to load answers for removed category:', error);
        setErrorMessage(
          `Kategorie byla odstraněna, ale nepodařilo se načíst její odpovědi. ${error.message || ''}`
        );
      }
    }
  }, [categories, fileId, indefinites, showSuccessMessage, removeAssignedAnswersForCategories]);

  const splitCategoryName = (category) => {
    return category
      .split('/')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
  };
  const handleSplitCategory = useCallback(async (indexToSplit) => {
    const oldCategory = categories[indexToSplit];
    const newCategoryParts = splitCategoryName(oldCategory);

    if (newCategoryParts.length < 2) {
      return;
    }

    setCategories((prev) => {
      const result = [];

      prev.forEach((category, index) => {
        if (index !== indexToSplit) {
          result.push(category);
          return;
        }

        newCategoryParts.forEach((newCategory) => {
          if (!result.includes(newCategory)) {
            result.push(newCategory);
          }
        });
      });

      return result;
    });

    setSelectedForMerge([]);

    setDeletedCategories((prev) =>
      prev.includes(oldCategory)
        ? prev
        : [...prev, oldCategory]
    );

    removeAssignedAnswersForCategories([oldCategory]);

    setCategoryRenames((prev) =>
      prev.filter(
        (rename) =>
          rename.old_name !== oldCategory &&
          rename.new_name !== oldCategory
      )
    );

    setAnswerCategoryAssignments((prev) =>
      prev.filter((assignment) => assignment.category !== oldCategory)
    );

    if (indefinites !== null && oldCategory) {
      try {
        const response = await loadCategoryAnswers({
          fileId,
          category: oldCategory,
        });

        const answersFromBackend =
          response?.answers ||
          response?.indefinites ||
          response?.uncategorized_answers ||
          [];

        if (answersFromBackend.length > 0) {
          setIndefinites((prev) =>
            mergeIndefinites(prev || [], answersFromBackend)
          );
        }

        showSuccessMessage(
          `Kategorie '${oldCategory}' byla rozdělena. Její původní odpovědi byly přesunuty mezi nezařazené.`
        );
      } catch (error) {
        console.error('Failed to load answers for split category:', error);
        setErrorMessage(
          `Kategorie byla rozdělena, ale nepodařilo se načíst její odpovědi. ${error.message || ''}`
        );
      }
    }
  }, [categories, fileId, indefinites, showSuccessMessage, removeAssignedAnswersForCategories]);

  const handleEditCategory = (index, newValue) => {
    const oldValue = categories[index];
    const newCategories = [...categories];
    newCategories[index] = newValue;
    setCategories(newCategories);
    updateAssignedAnswersCategory(oldValue, newValue);

    setCategoryRenames((prev) => {
      const existingRename = prev.find((rename) => rename.new_name === oldValue);

      if (existingRename) {
        if (existingRename.old_name === newValue) {
          return prev.filter((rename) => rename.old_name !== existingRename.old_name);
        }

        return prev.map((rename) =>
          rename.old_name === existingRename.old_name
            ? { ...rename, new_name: newValue }
            : rename
        );
      }

      if (!originalCategories.includes(oldValue) || oldValue === newValue) {
        return prev;
      }

      return [
        ...prev,
        {
          old_name: oldValue,
          new_name: newValue,
        },
      ];
    });
  };

  const toggleMergeSelection = (index) => {
    setSelectedForMerge((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  const handleMergeSelected = () => {
    if (selectedForMerge.length < 2) return;

    const sortedIndices = [...selectedForMerge].sort((a, b) => a - b);
    const firstIndex = sortedIndices[0];

    const mergedCategories = sortedIndices.map((i) => categories[i]);
    const mergedText = mergedCategories.join(' / ');

    setCategories((prev) =>
      prev
        .map((cat, i) => (i === firstIndex ? mergedText : cat))
        .filter((_, i) => i === firstIndex || !selectedForMerge.includes(i))
    );

    setAnswerCategoryAssignments((prev) =>
      prev.map((assignment) =>
        mergedCategories.includes(assignment.category)
          ? { ...assignment, category: mergedText }
          : assignment
      )
    );

    setCategoryRenames((prev) => {
      const withoutMergedCategories = prev.filter(
        (rename) =>
          !mergedCategories.includes(rename.old_name) &&
          !mergedCategories.includes(rename.new_name)
      );

      const newRenames = mergedCategories
        .filter((category) => originalCategories.includes(category))
        .map((category) => ({
          old_name: category,
          new_name: mergedText,
        }));

      return [...withoutMergedCategories, ...newRenames];
    });

    setSelectedForMerge([]);
  };

  const handleChangeFixedAssignmentCategory = useCallback((answer, newCategory) => {
    setAnswerCategoryAssignments((prev) =>
      prev.map((assignment) =>
        assignment.answer === answer
          ? { ...assignment, category: newCategory }
          : assignment
      )
    );

    setOpenFixedCategoryMenuAnswer(null);
    setFixedCategorySearch('');
  }, []);

  const handleAssignAnswerToCategory = useCallback((answer, category) => {
    setAnswerCategoryAssignments((prev) => [
      ...prev.filter((assignment) => assignment.answer !== answer),
      { answer, category },
    ]);

    setIndefinites((prev) => (prev || []).filter(([currentAnswer]) => currentAnswer !== answer));
    setOpenCategoryMenuAnswer(null);
    showSuccessMessage(`Odpověď '${answer}' byla přiřazena do kategorie '${category}'.`);
  }, [showSuccessMessage]);

  const toggleBulkAnswerSelection = useCallback((answer) => {
    setSelectedBulkAnswers((prev) =>
      prev.includes(answer)
        ? prev.filter((item) => item !== answer)
        : [...prev, answer]
    );
  }, []);
  const handleToggleBulkEditMode = useCallback(() => {
    setIsBulkEditMode((prev) => {
      const next = !prev;

      if (!next) {
        setSelectedBulkAnswers([]);
        setOpenBulkCategoryMenu(false);
        setBulkCategorySearch('');
      }

      return next;
    });
  }, []);
  const handleAssignBulkAnswersToCategory = useCallback((category) => {
    if (selectedBulkAnswers.length === 0) return;

    setAnswerCategoryAssignments((prev) => {
      const selectedSet = new Set(selectedBulkAnswers);

      return [
        ...prev.filter((assignment) => !selectedSet.has(assignment.answer)),
        ...selectedBulkAnswers.map((answer) => ({
          answer,
          category,
        })),
      ];
    });

    setIndefinites((prev) => {
      const selectedSet = new Set(selectedBulkAnswers);

      return (prev || []).filter(([answer]) => !selectedSet.has(answer));
    });

    showSuccessMessage(
      `${selectedBulkAnswers.length} odpovědí bylo přiřazeno do kategorie '${category}'.`
    );

    setSelectedBulkAnswers([]);
    setIsBulkEditMode(false);
    setOpenBulkCategoryMenu(false);
    setBulkCategorySearch('');
  }, [selectedBulkAnswers, showSuccessMessage]);

  const handleAssignRemainingToOther = useCallback(() => {
    if (!indefinites || indefinites.length === 0) return;

    const otherCategory = 'Ostatní';

    setCategories((prev) => (
      prev.includes(otherCategory) ? prev : [...prev, otherCategory]
    ));

    setAnswerCategoryAssignments((prev) => {
      const existingAnswers = new Set(indefinites.map(([answer]) => answer));

      return [
        ...prev.filter((assignment) => !existingAnswers.has(assignment.answer)),
        ...indefinites.map(([answer]) => ({
          answer,
          category: otherCategory,
        })),
      ];
    });

    setIndefinites([]);
    setOpenCategoryMenuAnswer(null);
    setIsBulkEditMode(false);
    setSelectedBulkAnswers([]);
    setOpenBulkCategoryMenu(false);
    setBulkCategorySearch('');
    showSuccessMessage(`Zbylé odpovědi byly přiřazeny do kategorie '${otherCategory}'.`);
  }, [indefinites, showSuccessMessage]);

  const handleSaveChanges = useCallback(async () => {
    if (!isStartEnabled) return;

    setIsLoading(true);
    setErrorMessage('');

    const payload = {
      fileId,
      ...getChangesPayload(),
    };

    try {
      const response = await saveCategoryChanges(payload);

      if (response?.balance !== undefined) {
        updateBalance(response.balance);
      }

      navigate(`/analysis/${fileId}/summary`);
    } catch (error) {
      console.error('Failed to save category changes:', error);
      setErrorMessage(error.message || 'Nepodařilo se uložit změny.');
      setIsLoading(false);
    }
  }, [fileId, getChangesPayload, isStartEnabled, navigate, updateBalance]);

  useEffect(() => {
    isMounted.current = true;
    let timeoutId = null;

    const fetchCategories = async () => {
      setErrorMessage('');

      try {
        const payload = { fileId };
        const response = await loadCategories(payload);

        if (!isMounted.current) return;

        if (response.status === 202 || response.status === 'suggesting') {
          timeoutId = setTimeout(fetchCategories, 3000);
        } else if (response.status === 'started') {
          onShowResult(fileId);
        } else if (response.categories && Array.isArray(response.categories)) {
          const lists = normalizeCategoryLists(response.categories);
          const prevLists = normalizeCategoryLists(response.prevCategories);

          const hasPrevCategories = prevLists.length > 0 && prevLists[0].length > 0;

          if (hasPrevCategories) {
            setPrevCategories(prevLists[0]);
            setPendingSuggestedLists(lists);
            setIsSelectingPrevOrNew(true);
            setIsSelectingList(false);
            setIsSuggesting(false);
          } else {
            applySuggestedLists(lists);
          }
        } else {
          setIsSuggesting(false);
        }

        if (response.balance !== undefined) {
          updateBalance(response.balance);
        }
        if (response.question !== undefined) {
          setQuestion(response.question);
        }

        if (Array.isArray(response.indefinites)) {
          setIndefinites(normalizeIndefinites(response.indefinites));
        }

        if (response.fixed && typeof response.fixed === 'object') {
          setAnswerCategoryAssignments(
            Object.entries(response.fixed).map(([answer, category]) => ({
              answer,
              category,
            }))
          );
        }
      } catch (error) {
        if (!isMounted.current) return;
        console.error('Failed to suggest categories:', error);
        setIsSuggesting(false);
        onShowResult(fileId);
      }
    };

    fetchCategories();

    return () => {
      isMounted.current = false;
      if (timeoutId) clearTimeout(timeoutId);
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  // When categories dropdown menu is open and the user clicks outside, then close the menu 
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryMenuRef.current && !categoryMenuRef.current.contains(event.target)) {
        setOpenCategoryMenuAnswer(null);
        setCategorySearch('');
      }

      if (fixedCategoryMenuRef.current && !fixedCategoryMenuRef.current.contains(event.target)) {
        setOpenFixedCategoryMenuAnswer(null);
        setFixedCategorySearch('');
      }

      if (bulkCategoryMenuRef.current && !bulkCategoryMenuRef.current.contains(event.target)) {
        setOpenBulkCategoryMenu(false);
        setBulkCategorySearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleStartAnalysis = useCallback(async () => {
    if (!isStartEnabled) return;

    setIsLoading(true);
    setErrorMessage('');

    const payload = {
      fileId,
      ...getChangesPayload(),
    };

    try {
      const response = await startAnalysis(payload);

      if (response && (response.code === 202 || response.file_id)) {
        onShowResult(response.file_id);
      } else {
        const msg = response.message || 'Nepodařilo se spustit analýzu. Zkontrolujte data a zkuste to znovu.';
        setErrorMessage(msg);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Analysis failed to start:', error);
      setErrorMessage(error.message || 'Došlo k chybě komunikace se serverem.');
      setIsLoading(false);
    }
  }, [isStartEnabled, getChangesPayload, onShowResult, fileId]);

  const names = ['Detailní', 'Vyvážené', 'Souhrnné'];

  const renderCategoriesEditor = () => (
    <div className="w-full space-y-4 mb-6 p-5 border border-blue-200 bg-blue-50/50 rounded-xl animate-fade-in shadow-sm">
      {!isSuggesting && isSelectingPrevOrNew && (
        <div className="flex flex-col animate-fade-in mt-2 w-full">
          <div className="bg-white rounded-xl border border-blue-200 shadow-sm p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Byly nalezeny kategorie použité v dřívější analýze
            </h3>

            <p className="text-gray-600 mb-5">
              Pro podobnou otázku už existuje seznam kategorií. Můžete použít tyto
              dřívější kategorie, nebo pokračovat s nově navrženými kategoriemi.
              Kategorie můžete poté dále upravovat.
            </p>

            <div className="mb-6 rounded-lg bg-gray-50 border border-gray-200 p-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-semibold text-gray-700">
                  Dřívější kategorie
                </h4>

                <span className="text-xs font-bold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                  Celkem: {prevCategories.length}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 max-h-56 overflow-y-auto">
                {prevCategories.map((category, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-sm rounded-lg shadow-sm"
                  >
                    {category}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ActionButton onClick={handleUsePrevCategories} className="w-full">
                Použít dřívější kategorie
              </ActionButton>

              <button
                onClick={handleUseNewCategories}
                className="w-full bg-white border border-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Použít nově navržené kategorie
              </button>
            </div>
          </div>
        </div>
      )}

      {!isSuggesting && !isSelectingList && suggestedCategoryLists.length > 1 && (
        <button
          onClick={() => setIsSelectingList(true)}
          className="flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800 bg-white border border-blue-200 px-3 py-1.5 rounded-lg transition-colors w-fit mb-3 shadow-sm"
        >
          <ArrowLeft size={16} className="mr-1.5" />
          Zpět na výběr variant
        </button>
      )}

      {!isSuggesting && isSelectingPrevOrNew ? (
        <></>
      ) : !isSuggesting && isSelectingList ? (
        <h3 className="font-semibold text-lg text-gray-800">
          Bylo navrženo více seznamů kategorií podle granularity kategorií. Vyberte jeden z nich, který poté můžete dále upravovat.
        </h3>
      ) : (
        <h3 className="font-semibold text-lg text-gray-800">
          Můžete přidat vlastní kategorie nebo upravit navržené.
        </h3>
      )}

      {isSuggesting &&
        <p>
          Kategorizace odpovědí je klíčová pro kvalitní analýzu, proto si na nich dáváme záležet. Navrhování může trvat několik desítek vteřin. Prosíme o trpělivost.
        </p>
      }

      {isSuggesting && (
        <div className="flex items-center justify-center p-8 bg-white rounded-lg border border-blue-100 shadow-sm">
          <LoaderCircle size={24} className="animate-spin text-blue-600 mr-3" />
          <span className="text-blue-800 font-medium">Načítám kategorie...</span>
        </div>
      )}

      {!isSuggesting && !isSelectingPrevOrNew && isSelectingList && (
        <div className="flex flex-col animate-fade-in mt-6 w-full">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
            {suggestedCategoryLists.map((list, index) => {
              const descriptions = [
                'Velmi detailní kategorie pro jemné rozlišení odpovědí.',
                'Střední cesta. Vyvážený detail pro většinu analýz.',
                'Širší, obecnější kategorie.',
              ];
              const isRecommended = index === 1;

              return (
                <div
                  key={index}
                  className={`relative flex flex-col bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all border-2 ${isRecommended ? 'border-blue-400 bg-blue-50/10' : 'border-gray-200 hover:border-blue-300'}`}
                >
                  {isRecommended && (
                    <span className="absolute -top-3 right-4 bg-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-sm">
                      Doporučeno
                    </span>
                  )}

                  <h4 className="font-bold text-gray-800 text-lg mb-1">
                    {names[index]}
                  </h4>
                  <p className="text-blue-600 font-bold mb-3">
                    {list.length} kategorií
                  </p>
                  <p className="text-gray-500 text-sm mb-6 flex-grow">
                    {descriptions[index] || 'Alternativní návrh kategorií.'}
                  </p>

                  <div className="flex flex-col gap-2 mt-auto">
                    <button
                      onClick={() => setExpandedListIndex(expandedListIndex === index ? null : index)}
                      className={`w-full font-semibold text-sm py-2 rounded-lg transition-colors border
                        ${expandedListIndex === index
                          ? 'bg-blue-100 text-blue-700 border-blue-200'
                          : 'text-blue-600 bg-white border-blue-100 hover:bg-blue-50'}`}
                    >
                      {expandedListIndex === index ? 'Skrýt detail' : 'Zobrazit detail'}
                    </button>

                    <ActionButton onClick={() => handleSelectList(list)} className="w-full py-2.5">
                      Použít tuto variantu
                    </ActionButton>
                  </div>
                </div>
              );
            })}
          </div>

          {expandedListIndex !== null && (
            <div className="mt-6 p-6 bg-white border-2 border-blue-200 rounded-xl shadow-inner animate-fade-in">
              <h4 className="font-bold text-gray-800 mb-4 flex items-center">
                Náhled kategorií pro {names[expandedListIndex]} kategorie
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
                {suggestedCategoryLists[expandedListIndex].map((cat, catIndex) => (
                  <div key={catIndex} className="flex items-start text-sm text-gray-700">
                    <span className="text-blue-400 mr-2 font-bold">•</span>
                    <span>{cat}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-gray-200 text-center">
            <p className="text-gray-500 text-sm mb-3">
              Nevyhovuje Vám ani jedna z navržených variant?
            </p>
            <button
              onClick={() => handleSelectList([])}
              className="text-gray-600 hover:text-blue-600 font-semibold transition-colors flex items-center justify-center mx-auto"
            >
              <Plus size={18} className="mr-1.5" />
              Zahodit návrhy a vytvořit si vlastní seznam od nuly
            </button>
          </div>
        </div>
      )}

      {!isSuggesting && !isSelectingPrevOrNew && !isSelectingList && (
        <div className="animate-fade-in">
          <div className="flex flex-wrap items-center gap-2 mb-6 p-2 bg-white border border-gray-200 shadow-sm rounded-xl">
            <button
              onClick={() => setActiveTool('add-single')}
              className={`flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-colors ${activeTool === 'add-single' ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <Plus size={16} className="mr-2" />
              Přidat kategorii
            </button>
            <button
              onClick={() => setActiveTool('add-bulk')}
              className={`flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-colors ${activeTool === 'add-bulk' ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <FileText size={16} className="mr-2" />
              Přidat hromadně
            </button>
            <button
              onClick={() => { setActiveTool('merge'); setSelectedForMerge([]); }}
              className={`flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-colors ${activeTool === 'merge' ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <Merge size={16} className="mr-2" />
              Sloučit kategorie
            </button>

            <div className="flex-grow"></div>

            {categories.length > 0 && (
              <button
                onClick={() => {
                  setCategories([]);
                  setSelectedForMerge([]);
                  setActiveTool('add-single');
                  setDeletedCategories((prev) => [...new Set([...prev, ...categories])]);
                  removeAssignedAnswersForCategories(categories);
                }}
                className="flex items-center px-4 py-2 rounded-lg font-medium text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 size={16} className="mr-2" />
                Smazat všechny
              </button>
            )}
          </div>

          {activeTool === 'add-single' && (
            <div className="flex space-x-2 animate-fade-in mb-6">
              <input
                type="text"
                value={currentCategory}
                onChange={(e) => setCurrentCategory(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                onPaste={handlePaste}
                placeholder="Zadejte název nové kategorie..."
                maxLength={255}
                className="flex-grow px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
              <ActionButton onClick={handleAddCategory} disabled={!currentCategory.trim()} className="!w-auto px-6">
                Přidat
              </ActionButton>
            </div>
          )}

          {activeTool === 'add-bulk' && (
            <div className="flex flex-col space-y-3 bg-white p-4 rounded-lg border border-gray-200 shadow-sm animate-fade-in mb-6">
              <textarea
                rows="4"
                value={bulkText}
                onChange={(e) => { setBulkText(e.target.value); setBulkPreview([]); }}
                placeholder="Vložte sem seznam kategorií. Každá kategorie musí být na novém řádku nebo oddělena tabulátorem."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />

              {bulkPreview.length === 0 ? (
                <ActionButton onClick={handleProcessBulkText} disabled={!bulkText.trim()} className="w-full">
                  Zobrazit náhled
                </ActionButton>
              ) : (
                <div className="space-y-4 animate-fade-in">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm font-semibold text-green-800 mb-3 flex items-center">
                      <CheckCircle size={18} className="mr-2" />
                      Nalezeno {bulkPreview.length} kategorií k přidání:
                    </p>
                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                      {bulkPreview.map((cat, i) => (
                        <span key={i} className="px-2.5 py-1 bg-white border border-green-300 text-green-800 text-sm font-medium rounded-md shadow-sm">
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setBulkPreview([])} className="w-1/3 bg-gray-100 text-gray-700 font-semibold py-2 rounded-lg hover:bg-gray-200 transition-colors border border-gray-300">
                      Zrušit náhled
                    </button>
                    <ActionButton onClick={handleConfirmBulkAdd} className="w-2/3">
                      Přidat kategorie do seznamu
                    </ActionButton>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTool === 'merge' && (
            <div className="mb-6 bg-blue-50 border border-blue-200 p-4 rounded-xl flex flex-col sm:flex-row justify-between items-center gap-4 animate-fade-in shadow-sm">
              <div>
                <h4 className="font-semibold text-blue-800 flex items-center">
                  <Merge size={18} className="mr-2" /> Režim slučování aktivní
                </h4>
                <p className="text-sm text-blue-600 mt-1">Zaškrtněte kategorie v seznamu níže, které chcete sloučit do jedné.</p>
              </div>
              <ActionButton
                onClick={handleMergeSelected}
                disabled={selectedForMerge.length < 2}
                className="!w-auto px-6 whitespace-nowrap"
              >
                Sloučit vybrané ({selectedForMerge.length})
              </ActionButton>
            </div>
          )}

          <div className="mt-2 pt-5 border-t border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold text-gray-700">Aktuální seznam kategorií</h4>
              <span className="text-xs font-bold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                Celkem: {categories.length}
              </span>
            </div>

            {categories.length > 0 ? (
              <ul className="space-y-2 max-h-[400px] overflow-y-auto p-1">
                {categories.map((item, index) => {
                  const isSelected = selectedForMerge.includes(index);

                  return (
                    <li
                      key={`${item}-${index}`}
                      className={`flex items-center p-2 rounded-lg border shadow-sm transition-all
                        ${activeTool === 'merge' && isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}
                        ${activeTool === 'merge' ? 'cursor-pointer hover:border-blue-400' : 'hover:border-blue-300'}
                      `}
                      onClick={() => activeTool === 'merge' && toggleMergeSelection(index)}
                    >
                      {activeTool === 'merge' && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="w-5 h-5 ml-2 mr-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer pointer-events-none"
                        />
                      )}

                      <input
                        type="text"
                        value={item}
                        onChange={(e) => handleEditCategory(index, e.target.value)}
                        readOnly={activeTool === 'merge'}
                        maxLength={255}
                        className={`flex-grow p-1.5 border border-transparent hover:border-gray-200 focus:border-blue-400 rounded-md text-sm outline-none transition-colors mr-3
                          ${activeTool === 'merge' ? 'bg-transparent pointer-events-none cursor-pointer' : 'bg-transparent'}`}
                        placeholder="Zadejte název kategorie"
                      />

                      {activeTool !== 'merge' && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {item.includes('/') && (
                            <button
                              type="button"
                              onClick={() => handleSplitCategory(index)}
                              className="p-2 text-purple-600 rounded-md hover:bg-purple-50 hover:text-purple-800 transition-colors"
                              title="Rozdělit kategorii podle lomítka"
                            >
                              <Scissors size={18} />
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => handleRemoveCategory(index)}
                            className="p-2 text-red-500 rounded-md hover:bg-red-50 hover:text-red-700 transition-colors"
                            title="Odstranit kategorii"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="text-center p-6 bg-white rounded-lg border border-dashed border-gray-300">
                <p className="text-gray-500">Zatím nebyly přidány žádné kategorie.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderIndefinitesTab = () => (
    <div>
    <div className="w-full flex flex-col animate-fade-in mb-8">
      <div className="flex items-start gap-2 rounded-lg mb-4 text-gray-500">
        <Info className="w-5 h-5 flex-shrink-0 text-blue-500 mt-0.5" />
        <p>
          Zde jsou odpovědi, které zatím nejsou přiřazené k žádné kategorii.
          Každou odpověď můžete ručně přiřadit do existující kategorie, nebo všechny zbylé odpovědi přesunout do kategorie Ostatní.
        </p>
      </div>

      <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-blue-900">
              Hromadná úprava
            </p>
            <p className="text-sm text-blue-800 mt-1">
              Zapněte hromadnou úpravu, označte více odpovědí a potom jim najednou přiřaďte stejnou kategorii.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <button
              type="button"
              onClick={handleToggleBulkEditMode}
              disabled={indefiniteCount === 0}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                isBulkEditMode
                  ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isBulkEditMode ? 'Zrušit hromadnou úpravu' : 'Hromadná úprava'}
            </button>

            {isBulkEditMode && (
              <div className="relative" ref={bulkCategoryMenuRef}>
                <button
                  type="button"
                  onClick={() => {
                    if (selectedBulkAnswers.length === 0) return;
                    setOpenBulkCategoryMenu((prev) => !prev);
                    setBulkCategorySearch('');
                  }}
                  disabled={selectedBulkAnswers.length === 0}
                  className="px-4 py-2 rounded-lg bg-blue-50 border border-blue-300 text-blue-700 font-semibold text-sm hover:bg-blue-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  Přidat do kategorie ({selectedBulkAnswers.length})
                </button>

                {openBulkCategoryMenu && (
                  <div className="absolute right-0 top-full z-40 mt-2 w-72 rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden">
                    <div className="p-2 border-b border-gray-100">
                      <input
                        type="text"
                        value={bulkCategorySearch}
                        onChange={(e) => setBulkCategorySearch(e.target.value)}
                        placeholder="Vyhledat kategorii..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                        autoFocus
                      />
                    </div>

                    <div className="max-h-64 overflow-y-auto">
                      {categories.length > 0 ? (
                        categories
                          .filter((category) =>
                            category
                              .toLowerCase()
                              .includes(bulkCategorySearch.trim().toLowerCase())
                          )
                          .map((category, index) => (
                            <button
                              key={`${category}-${index}`}
                              type="button"
                              onClick={() => handleAssignBulkAnswersToCategory(category)}
                              className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                            >
                              {category}
                            </button>
                          ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500">
                          Nejdříve přidejte alespoň jednu kategorii.
                        </div>
                      )}

                      {categories.length > 0 &&
                        categories.filter((category) =>
                          category
                            .toLowerCase()
                            .includes(bulkCategorySearch.trim().toLowerCase())
                        ).length === 0 && (
                          <div className="px-4 py-3 text-sm text-gray-500">
                            Žádná kategorie nenalezena.
                          </div>
                        )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {isBulkEditMode && (
          <div className="mt-3 text-sm text-blue-800">
            Vybráno: <span className="font-semibold">{selectedBulkAnswers.length}</span>{' '}
            odpovědí. Kliknutím na odpověď ji přidáte do výběru nebo odeberete.
          </div>
        )}
      </div>

      <div
        className="flex-grow bg-gray-50 p-4 rounded-lg border overflow-y-auto"
        style={{ maxHeight: '600px' }}
      >
        {indefiniteCount > 0 ? (
          <ul className="space-y-3">
            {indefinites.map(([answer, count]) => {
              const isSelectedForBulk = selectedBulkAnswers.includes(answer);

              return (
                <li
                  key={answer}
                  onClick={() => {
                    if (isBulkEditMode) {
                      toggleBulkAnswerSelection(answer);
                    }
                  }}
                  className={`text-gray-800 flex justify-between items-center text-base bg-white p-3 rounded-md shadow-sm border transition-colors ${
                    isBulkEditMode
                      ? 'cursor-pointer hover:bg-blue-50'
                      : ''
                  } ${
                    isSelectedForBulk
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100'
                      : 'border-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0 mr-4">
                    {isBulkEditMode && (
                      <input
                        type="checkbox"
                        checked={isSelectedForBulk}
                        onChange={() => toggleBulkAnswerSelection(answer)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    )}

                    <span className="font-medium break-words">{answer}</span>
                  </div>

                <div className="flex items-center gap-3">
                  <span className="font-bold text-blue-600 bg-blue-100 px-3 py-1 rounded-full text-sm">
                    {count}
                  </span>

                  {!isBulkEditMode && (
                    <div className="relative" ref={openCategoryMenuAnswer === answer ? categoryMenuRef : null}>
                      <button
                        type="button"
                        onClick={() => {
                          setOpenCategoryMenuAnswer(
                            openCategoryMenuAnswer === answer ? null : answer
                          );
                          setCategorySearch('');
                        }}
                        className="px-3 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm font-medium"
                      >
                        Přidat do kategorie
                      </button>

                      {openCategoryMenuAnswer === answer && (
                        <div className="absolute right-0 top-full z-30 mt-2 w-72 rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden">
                          <div className="p-2 border-b border-gray-100">
                            <input
                              type="text"
                              value={categorySearch}
                              onChange={(e) => setCategorySearch(e.target.value)}
                              placeholder="Vyhledat kategorii..."
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                              autoFocus
                            />
                          </div>

                          <div className="max-h-64 overflow-y-auto">
                            {categories.length > 0 ? (
                              categories
                                .filter((category) =>
                                  category
                                    .toLowerCase()
                                    .includes(categorySearch.trim().toLowerCase())
                                )
                                .map((category, index) => (
                                  <button
                                    key={`${category}-${index}`}
                                    type="button"
                                    onClick={() => {
                                      handleAssignAnswerToCategory(answer, category);
                                      setOpenCategoryMenuAnswer(null);
                                      setCategorySearch('');
                                    }}
                                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                                  >
                                    {category}
                                  </button>
                                ))
                            ) : (
                              <div className="px-4 py-3 text-sm text-gray-500">
                                Nejdříve přidejte alespoň jednu kategorii.
                              </div>
                            )}

                            {categories.length > 0 &&
                              categories.filter((category) =>
                                category
                                  .toLowerCase()
                                  .includes(categorySearch.trim().toLowerCase())
                              ).length === 0 && (
                                <div className="px-4 py-3 text-sm text-gray-500">
                                  Žádná kategorie nenalezena.
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </li>
              );
            })}
          </ul>
        ) : (
          <div className="text-center p-8 bg-white rounded-lg border border-dashed border-gray-300">
            <CheckCircle className="mx-auto mb-3 text-green-500" size={32} />
            <p className="font-semibold text-gray-700">Žádné nezařazené odpovědi.</p>
            <p className="text-sm text-gray-500 mt-1">
              Všechny odpovědi jsou aktuálně přiřazené do kategorií.
            </p>
          </div>
        )}
      </div>

      <div className="mt-4">
        <ActionButton
          onClick={handleAssignRemainingToOther}
          disabled={indefiniteCount === 0}
          className="w-full"
        >
          Zbylé odpovědi přiřadit do kategorie Ostatní
        </ActionButton>
      </div>
    </div>

    <div className="mt-6 border-t border-gray-200 pt-5">
      <h3 className="text-lg font-semibold text-gray-800">
        Ručně zařazené odpovědi
      </h3>

      <p className="mt-1 text-sm text-gray-600">
        Zde jsou odpovědi, které už byly ručně přiřazené do kategorií.
        Jejich přiřazení můžete změnit.
      </p>

      {answerCategoryAssignments.length === 0 ? (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
          Zatím nejsou uložené žádné ručně zařazené odpovědi.
        </div>
      ) : (
        <div className="mt-4 max-h-80 overflow-y-auto pr-2 space-y-3">
          {answerCategoryAssignments.map((assignment) => {
            const filteredCategories = categories.filter((category) =>
              category
                .toLowerCase()
                .includes(fixedCategorySearch.trim().toLowerCase())
            );

            return (
              <div
                key={assignment.answer}
                className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3"
              >
                <div className="min-w-0">
                  <p className="break-words text-sm font-medium text-gray-800">
                    {assignment.answer}
                  </p>

                  <p className="mt-1 text-xs text-gray-500">
                    Aktuálně přiřazeno:{' '}
                    <span className="font-semibold text-gray-700">
                      {assignment.category}
                    </span>
                  </p>
                </div>

                <div
                  className="relative flex-shrink-0"
                  ref={
                    openFixedCategoryMenuAnswer === assignment.answer
                      ? fixedCategoryMenuRef
                      : null
                  }
                >
                  <button
                    type="button"
                    onClick={() => {
                      setOpenFixedCategoryMenuAnswer(
                        openFixedCategoryMenuAnswer === assignment.answer
                          ? null
                          : assignment.answer
                      );
                      setFixedCategorySearch('');
                    }}
                    className="px-3 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm font-medium"
                  >
                    Změnit kategorii
                  </button>

                  {openFixedCategoryMenuAnswer === assignment.answer && (
                    <div className="absolute right-0 top-full z-30 mt-2 w-72 rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden">
                      <div className="p-2 border-b border-gray-100">
                        <input
                          type="text"
                          value={fixedCategorySearch}
                          onChange={(e) => setFixedCategorySearch(e.target.value)}
                          placeholder="Vyhledat kategorii..."
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                          autoFocus
                        />
                      </div>

                      <div className="max-h-64 overflow-y-auto">
                        {filteredCategories.length > 0 ? (
                          filteredCategories.map((category, index) => (
                            <button
                              key={`${category}-${index}`}
                              type="button"
                              onClick={() =>
                                handleChangeFixedAssignmentCategory(
                                  assignment.answer,
                                  category
                                )
                              }
                              className={`block w-full px-4 py-2 text-left text-sm hover:bg-blue-50 hover:text-blue-700 ${
                                category === assignment.category
                                  ? 'bg-blue-50 text-blue-700 font-semibold'
                                  : 'text-gray-700'
                              }`}
                            >
                              {category}
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-sm text-gray-500">
                            Žádná kategorie nenalezena.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
    </div>
  );

  return (
    <>
      {successMessage && createPortal(
        <div className="fixed top-10 left-1/2 transform -translate-x-1/2 z-[100] bg-green-50 border border-green-200 text-green-800 px-5 py-3 rounded-xl shadow-2xl flex items-center animate-fade-in">
          <CheckCircle size={20} className="mr-2 text-green-600 flex-shrink-0" />
          <span className="font-medium text-sm md:text-base">{successMessage}</span>
        </div>,
        document.body
      )}

      <div className="flex flex-col items-center w-full flex-grow">
        <div className="w-full text-center mb-8">
          {indefinites !== null ? (
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Upřesnění výsledků</h2>
              <p className="text-gray-500 mt-2">
                Upravte kategorie nebo ručně přiřaďte nezařazené odpovědi.
              </p>
            </div>
          ) : (
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Vyberte a upravte kategorie</h2>
              <p>Krok 4</p>
              <div className="items-start gap-2 rounded-lg mb-4 text-gray-500">
                <p>Otázka: '{question}'</p>
              </div>
            </div>
          )}
        </div>

        {indefinites !== null && (
          <TabToggle
            activeTab={activeTab}
            onChange={setActiveTab}
            indefiniteCount={indefiniteCount}
          />
        )}

        {indefinites === null || activeTab === 'categories'
          ? renderCategoriesEditor()
          : renderIndefinitesTab()
        }
      </div>

      {errorMessage && (
        <div className="w-full max-w-2xl mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start animate-fade-in text-sm shadow-sm">
          <AlertCircle size={18} className="mr-2 flex-shrink-0 mt-0.5" />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="w-full pt-6 mt-4 border-t border-gray-200">
        {indefinites !== null ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => navigate(`/analysis/${fileId}/summary`)}
                  className="w-full h-16 flex items-center justify-center bg-gray-200 text-gray-800 font-semibold px-4 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Zpět
                </button>

                <p className="text-xs text-gray-500 leading-relaxed">
                  Vrátí vás zpět na souhrn výsledků bez uložení aktuálních změn.
                </p>
              </div>

              <div className="space-y-2 sm:col-span-2">
                <button
                  type="button"
                  onClick={handleSaveChanges}
                  disabled={!isStartEnabled || isLoading}
                  className="w-full h-16 flex items-center justify-center bg-blue-600 text-white font-bold px-5 rounded-lg shadow-md hover:bg-blue-700 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                  title="Uloží změny kategorií a ruční přiřazení odpovědí bez spuštění nové analýzy."
                >
                  Uložit změny
                </button>

                <p className="text-xs text-gray-600 leading-relaxed">
                  Uloží sloučení, přejmenování i odstranění kategorií a ruční přiřazení odpovědí - DOPORUČUJEME.
                </p>
              </div>

              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(true)}
                  disabled={!isStartEnabled || isLoading}
                  className="w-full h-16 flex items-center justify-center border border-orange-300 bg-orange-50 text-orange-700 font-semibold px-4 rounded-lg hover:bg-orange-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  title="Uloží změny a spustí novou automatickou analýzu."
                >
                  Provést novou analýzu
                </button>

                <p className="text-xs text-orange-700 leading-relaxed">
                  Uloží změny a znovu spustí automatické zařazení odpovědí podle aktuálního seznamu kategorií.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-end">
            <ActionButton
              onClick={() => setShowConfirmModal(true)}
              disabled={!isStartEnabled}
              className="w-full sm:w-1/2"
            >
              Spustit analýzu
            </ActionButton>
          </div>
        )}
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 animate-fade-in text-center">

            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Info size={32} className="text-blue-600" />
            </div>

            <h3 className="text-xl font-bold text-gray-800 mb-2">
              {indefinites !== null ? 'Opravdu chcete spustit analýzu znovu?' : 'Opravdu chcete spustit analýzu?'}
            </h3>

            <p className="text-gray-600 mb-6">
              {indefinites !== null
                ? 'Předešlé výsledky nebudou brány v potaz a analýza se celá provede znovu na novém seznamu kategorií. Pouze ručně přiřazené odpovědi jsou uloženy. Doporučujeme POUZE při zásadních změnách v kategorií (tvorba nových kategorií). V případě ručních úprav klikněte na "Uložit změny".'
                : 'Po spuštění již nebude možné upravovat kategorie pro tuto analýzu.'}
            </p>

            <div className="flex gap-3 w-full">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="w-1/2 bg-white border border-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Zrušit
              </button>
              <ActionButton
                onClick={() => {
                  setShowConfirmModal(false);
                  handleStartAnalysis();
                }}
                className="w-1/2"
              >
                {indefinites !== null ? 'Chci provést novou analýzu' : 'Ano, spustit'}
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CategoriesStep;